import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  SelectChangeEvent
} from '@mui/material';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../contexts/AuthContext';

const ForceProfileCreation: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: 'Employee' as UserRole,
    department: 'HR'
  });

  useEffect(() => {
    // Pre-fill the form with email username if available
    if (currentUser?.email) {
      const emailUsername = currentUser.email.split('@')[0];
      setFormData(prev => ({
        ...prev,
        firstName: emailUsername
      }));
    }
  }, [currentUser]);

  // Handle text field changes
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle select field changes
  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name as string]: value
    }));
  };

  const handleCreateProfile = async () => {
    if (!currentUser) {
      setError('You must be logged in to create a profile');
      return;
    }

    if (!formData.firstName || !formData.lastName) {
      setError('First name and last name are required');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Create a basic profile
      const profileData = {
        uid: currentUser.uid,
        email: currentUser.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        department: formData.department,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Write directly to Firestore
      await setDoc(doc(db, 'users', currentUser.uid), profileData, { merge: true });
      
      setSuccess(true);

      // Force reload after 1.5 seconds to refresh auth state
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err: any) {
      console.error('Error creating profile:', err);
      setError(err.message || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>Force Profile Creation</Typography>
        <Typography color="text.secondary" paragraph>
          Use this tool as a last resort when your profile can't be loaded or created automatically.
        </Typography>

        <Paper sx={{ p: 3, my: 3 }}>
          <Typography variant="h6" gutterBottom>Current State</Typography>
          <Box sx={{ mb: 2 }}>
            <Typography>
              <strong>Authenticated:</strong> {currentUser ? 'Yes' : 'No'}
            </Typography>
            {currentUser && (
              <>
                <Typography>
                  <strong>User ID:</strong> {currentUser.uid}
                </Typography>
                <Typography>
                  <strong>Email:</strong> {currentUser.email}
                </Typography>
              </>
            )}
            <Typography>
              <strong>Profile Loaded:</strong> {userProfile ? 'Yes' : 'No'}
            </Typography>
          </Box>

          {!currentUser && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              You need to be logged in to create a profile. Please <Button size="small" onClick={() => window.location.href = '/login'}>login</Button> first.
            </Alert>
          )}
        </Paper>

        {currentUser && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Create Basic Profile</Typography>
            <Divider sx={{ mb: 3 }} />

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleTextChange}
                required
                fullWidth
              />

              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleTextChange}
                required
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  name="role"
                  value={formData.role}
                  onChange={handleSelectChange}
                  label="Role"
                >
                  <MenuItem value="Administrator">Administrator</MenuItem>
                  <MenuItem value="Manager">Manager</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Employee">Employee</MenuItem>
                  <MenuItem value="Accountant">Accountant</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleSelectChange}
                  label="Department"
                >
                  <MenuItem value="Engineering">Engineering</MenuItem>
                  <MenuItem value="Product">Product</MenuItem>
                  <MenuItem value="Design">Design</MenuItem>
                  <MenuItem value="Marketing">Marketing</MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                  <MenuItem value="Operations">Operations</MenuItem>
                </Select>
              </FormControl>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Profile created successfully! Reloading...
                </Alert>
              )}

              <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateProfile}
                  disabled={loading || !formData.firstName || !formData.lastName}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Creating...' : 'Create Profile'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => window.location.href = '/profile'}
                >
                  Back to Profile
                </Button>
              </Box>
            </Box>
          </Paper>
        )}
      </Box>
    </Container>
  );
};

export default ForceProfileCreation; 